import { useState } from 'react'
import { Download, FileSpreadsheet, FileText, Layers, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatDateRangeHuman } from '@/lib/utils'
import type { ReportsExportId } from '@/types'

interface ExportButtonProps {
  report: ReportsExportId
  reportLabel: string
  fromISO: string
  toISO: string
  /** Optional extra params to pass through to the CSV exporter (e.g. items
   *  search/category for the Items tab). */
  extraParams?: Record<string, string>
  /**
   * Optional handler invoked when the user picks "Print PDF". The owning tab
   * should open a print-friendly view of its current data — keeping the
   * backend dependency-free while letting the user save as PDF from the
   * native browser print dialog.
   */
  onPrintPdf?: () => void
  /**
   * Optional handler for an order-wise + line-wise audit workbook (.xlsx). When
   * provided, an "Order detail (Excel)" item is added — the auditable export, as
   * opposed to the per-day summary CSV. Should stream the file itself.
   */
  onOrderDetailExport?: () => Promise<void> | void
  /**
   * Optional handler for the bundled "Period Pack" workbook — one .xlsx with
   * every report as a tab. When provided, a dedicated item is added under a
   * separator. Used by the Overview (landing) tab as the month-end deliverable.
   */
  onPeriodPack?: () => Promise<void> | void
  size?: 'sm' | 'default'
  variant?: 'outline' | 'default' | 'ghost' | 'secondary'
}

/** Reports that backend ExportReport (reports.go) can stream as CSV. */
const CSV_CAPABLE_REPORTS: ReadonlySet<ReportsExportId> = new Set<ReportsExportId>([
  'daily_sales',
  'items',
  'tables',
  'party_size',
  'hourly',
  'overview',
  'refunds',
])

/**
 * Re-usable Export button for any report tab. CSV downloads stream from the
 * backend; PDF goes through the browser's native print dialog (the calling
 * tab decides what to render in the print window).
 */
export function ExportButton({
  report,
  reportLabel,
  fromISO,
  toISO,
  extraParams,
  onPrintPdf,
  onOrderDetailExport,
  onPeriodPack,
  size = 'sm',
  variant = 'outline',
}: ExportButtonProps) {
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)

  const handleCsv = async () => {
    setBusy(true)
    try {
      await apiClient.exportReportCsv(report, fromISO, toISO, extraParams ?? {})
      toast({
        title: 'CSV downloaded',
        description: `${reportLabel} • ${formatDateRangeHuman(fromISO, toISO)}`,
      })
    } catch (e) {
      toast({
        title: 'Export failed',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      })
    } finally {
      setBusy(false)
    }
  }

  const handleExcel = async () => {
    setBusy(true)
    try {
      await apiClient.exportReportXlsx(report, fromISO, toISO, extraParams ?? {})
      toast({
        title: 'Excel downloaded',
        description: `${reportLabel} • ${formatDateRangeHuman(fromISO, toISO)}`,
      })
    } catch (e) {
      toast({
        title: 'Export failed',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      })
    } finally {
      setBusy(false)
    }
  }

  const handlePeriodPack = async () => {
    if (!onPeriodPack) return
    setBusy(true)
    try {
      await onPeriodPack()
      toast({
        title: 'Period Pack exported',
        description: `${formatDateRangeHuman(fromISO, toISO)} • all reports in one workbook`,
      })
    } catch (e) {
      toast({
        title: 'Export failed',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      })
    } finally {
      setBusy(false)
    }
  }

  const handleOrderDetail = async () => {
    if (!onOrderDetailExport) return
    setBusy(true)
    try {
      await onOrderDetailExport()
      toast({
        title: 'Order detail exported',
        description: `${formatDateRangeHuman(fromISO, toISO)} • Excel workbook`,
      })
    } catch (e) {
      toast({
        title: 'Export failed',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={busy} className="gap-2">
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">Export {reportLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* F-RPT-N31: only reports with a server-side CSV writer get the item.
            Tax and Staff are Excel-only (multi-sheet); offering CSV there
            produced a 200 with a JSON error inside the file. */}
        {CSV_CAPABLE_REPORTS.has(report) && (
          <DropdownMenuItem onClick={handleCsv}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Summary CSV
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleExcel}>
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Detailed Excel (.xlsx)
        </DropdownMenuItem>
        {onOrderDetailExport && (
          <DropdownMenuItem onClick={handleOrderDetail}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Order detail (Excel)
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => onPrintPdf?.()}
          disabled={!onPrintPdf}
        >
          <FileText className="w-4 h-4 mr-2" />
          Print / Save as PDF
        </DropdownMenuItem>
        {onPeriodPack && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handlePeriodPack}>
              <Layers className="w-4 h-4 mr-2" />
              Full Period Pack (.xlsx)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
